import { appNoOp } from '@actions/app.action';
import {
  cmsCopyLoaded,
  cmsCopyLoadFailed,
  cmsCopyLoadFlowFailed,
  cmsLoadCopy,
  cmsLoadFlowCopy,
  cmsLoadDynamicCopy,
  cmsDynamicCopyLoaded,
  cmsDynamicCopyLoadFailed,
  resetCms, updateActiveDynamicPage,
} from '@actions/cms.action';
import { updateCultureCode } from '@actions/culture.action';
import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import {
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  FlowBaseUrlEnum,
} from '@constants/app.constants';
import {
  CmsDynamicFeatureCopy,
  CmsDynamicFlows,
  CmsDynamicKeys,
  CmsDynamicSubFlows,
  CmsFeatureCopy,
  CmsKeys,
  ContentPageKeys,
  DYNAMIC, FeaturePageKeys,
} from '@constants/cms.constants';
import { AppCopy } from '@models/cms/app.model';
import { EconomyCopy } from '@models/cms/cms-dynamic/experience/cabins/cabins.model';
import { FlightsCopy } from '@models/cms/flights.model';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { selectCmsFeatureState } from '@reducers';
import { State } from '@reducers/cms.reducer';
import { selectPageCacheFlowMap } from '@selectors/cms.selector';
import {
  selectAppFlowPathName,
  selectRouteParam,
} from '@selectors/router.selector';
import { CmsService } from '@services/cms/cms.service';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { getCultureCodeParts } from '@utils/data-utils';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

@Injectable()
export class CmsEffect {
  private readonly gbs = inject(GrowthBookService);
  private readonly gbf = GrowthBookFeatureKeys;

  constructor(
    private actions$: Actions,
    private store: Store,
    private cmsService: CmsService,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  storeCultureChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateCultureCode),
      mergeMap((action) => {
        // only reset CMS if the effective
        // front end culture changed from the update
        this.doc.documentElement.lang = getCultureCodeParts(
          action.code,
        ).language;

        return of(
          resetCms({
            culture: action.code,
          }),
        );
      }),
    );
  });

  loadCmsData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsLoadCopy),
      concatLatestFrom(() => [
        this.store.select(selectCmsFeatureState),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)),
        this.store.select(selectAppFlowPathName),
        this.store.select(selectPageCacheFlowMap),
      ]),
      mergeMap(([action, cmsData, cultureCode, flow, cacheMap]) => {
        const cachedData = cmsData[action.key] as CmsFeatureCopy;
        const flowBase = flow ?? FlowBaseUrlEnum.BOOK;
        if ((cachedData && cacheMap[action.key] === flowBase) || !cultureCode) {
          return of(appNoOp());
        }
        return this.cmsService
          .getFeature(flowBase, action.key, cultureCode)
          .pipe(
            map((data) => {
              let copy = data;
              if (action.key === CmsKeys.Flights) {
                if (
                  this.gbs.growthBook.isOn(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT) &&
                  this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT, '') === 'b'
                ) {
                  copy = copy as FlightsCopy;
                  const carryOnCopy = copy.fareAvailabilityRibbon.tiles.ec.benefits[0]?.[1];
                  const econLightBenefits = copy.fareAvailabilityRibbon.tiles.el.benefits[0];
                  const newEconBenefits = carryOnCopy && econLightBenefits && [...econLightBenefits.slice(0, 1), carryOnCopy, ...econLightBenefits.slice(1, econLightBenefits.length)];
                  if (newEconBenefits) {
                    copy = {
                      ...copy,
                      fareAvailabilityRibbon: {
                        ...copy.fareAvailabilityRibbon,
                        tiles: {
                          ...copy.fareAvailabilityRibbon.tiles,
                          el: {
                            ...copy.fareAvailabilityRibbon.tiles.el,
                            benefits: [newEconBenefits]
                          }
                        }
                      }
                    }
                  }
                }
                if (
                this.gbs.growthBook.isOn(this.gbf.FEAT_US930_FREE_SEAT_PREMIUM_CLASSIC) &&
                this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US930_FREE_SEAT_PREMIUM_CLASSIC, '') === 'b'
              ) {
                copy = copy as FlightsCopy;
                const seatSelectionCopy = copy.fareAvailabilityRibbon.tiles.pp.benefits[0]?.[0];
                const premClassicBenefits = copy.fareAvailabilityRibbon.tiles.pc.benefits[0];
                const newPremClassicBenefits = seatSelectionCopy && premClassicBenefits && [seatSelectionCopy, ...premClassicBenefits.slice()];
                if (newPremClassicBenefits) {
                  copy = {
                    ...copy,
                    fareAvailabilityRibbon: {
                      ...copy.fareAvailabilityRibbon,
                      tiles: {
                        ...copy.fareAvailabilityRibbon.tiles,
                        pc: {
                          ...copy.fareAvailabilityRibbon.tiles.pc,
                          benefits: [newPremClassicBenefits]
                        }
                      }
                    }
                  }
                }
              }
            }

              return cmsCopyLoaded({
                key: action.key,
                flow: flowBase as FlowBaseUrlEnum,
                data: copy,
              });
            }),
            catchError(() =>
              of(
                cmsCopyLoadFailed({
                  key: action.key,
                }),
              ),
            ),
          );
      }),
    );
  });

  loadCmsDataFromLocal$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsCopyLoadFailed),
      concatLatestFrom(() => [this.store.select(selectAppFlowPathName)]),
      mergeMap(([action, flow]) => {
        const flowBase = flow ?? FlowBaseUrlEnum.BOOK;
        return this.cmsService.getFeatureLocal(flowBase, action.key).pipe(
          map((data) => {
            return cmsCopyLoaded({
              key: action.key,
              flow: flowBase as FlowBaseUrlEnum,
              data,
            });
          }),
        );
      }),
    );
  });

  loadCmsDynamicData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsLoadDynamicCopy),
      concatLatestFrom(() => [
        this.store.select(selectCmsFeatureState),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([action, cmsData, cultureCode]) => {
        const isValidDynamicflow = Object.values(CmsDynamicFlows).find(
          (v) => v.toLowerCase() === action.flowKey.toLowerCase(),
        );

        if (!isValidDynamicflow || !cultureCode) {
          return of(appNoOp());
        }

        const flowData = cmsData?.[DYNAMIC]?.[
          action.flowKey
        ] as CmsDynamicFeatureCopy;

        const subFlowData =
          action.subFlowKey &&
          (flowData?.[action.subFlowKey] as CmsDynamicFeatureCopy);

        const cachedData = action.cmsKey
          ? (subFlowData?.[action.cmsKey] as CmsDynamicFeatureCopy)
          : subFlowData
          ? subFlowData
          : !action.subFlowKey
          ? flowData
          : null;

        if (cachedData) {
          return of(appNoOp());
        }

        return this.cmsService
          .getDynamicFeature(
            action.flowKey,
            action.subFlowKey,
            action.cmsKey,
            cultureCode,
          )
          .pipe(
            map((data) => {
              let copy = data;
              if (action.flowKey === CmsDynamicFlows.Experience && action.subFlowKey === CmsDynamicSubFlows.Cabins && action.cmsKey === CmsDynamicKeys.Economy &&
                this.gbs.growthBook.isOn(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT) &&
                this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT, '') === 'b'
              ) {
                copy = copy as EconomyCopy;
                const carryOnCopy = copy.fareAvailabilityRibbon?.tiles.ec.benefits[0]?.[1];
                const econLightBenefits = copy.fareAvailabilityRibbon?.tiles.el.benefits[0];
                const newEconBenefits = carryOnCopy && econLightBenefits && [...econLightBenefits.slice(0, 1), carryOnCopy, ...econLightBenefits.slice(1, econLightBenefits.length)];
                if (newEconBenefits) {
                  copy = {
                    ...copy,
                    fareAvailabilityRibbon: {
                      ...copy.fareAvailabilityRibbon,
                      tiles: {
                        ...copy.fareAvailabilityRibbon?.tiles,
                        el: {
                          ...copy.fareAvailabilityRibbon?.tiles.el,
                          benefits: [newEconBenefits]
                        }
                      }
                    }
                  } as EconomyCopy
                }
              }
              return cmsDynamicCopyLoaded({
                flow: action.flowKey,
                subFlow: action.subFlowKey,
                key: action.cmsKey,
                data: copy,
              });
            }),
            catchError(() =>
              of(
                cmsDynamicCopyLoadFailed({
                  flow: action.flowKey,
                  subFlow: action.subFlowKey,
                  key: action.cmsKey,
                }),
              ),
            ),
          );
      }),
    );
  });

  loadCmsDynamicDataFromLocal$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsDynamicCopyLoadFailed),
      mergeMap((action) => {
        return this.cmsService
          .getDynamicFeatureLocal(action.flow, action.subFlow, action.key)
          .pipe(
            map((data) => {
              return cmsDynamicCopyLoaded({
                key: action.key,
                flow: action.flow,
                subFlow: action.subFlow,
                data,
              });
            }),
          );
      }),
    );
  });

  loadCmsFlowData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsLoadFlowCopy),
      concatLatestFrom(() => [
        this.store.select(selectCmsFeatureState),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)),
      ]),
      mergeMap(
        ([action, cmsData, cultureCode]: [
          { key?: CmsKeys, contentPageKey?: ContentPageKeys, dynamicPageKey?: string, featurePageKey?: FeaturePageKeys },
          State,
          string | undefined,
        ]) => {
          if (!action.key && !action.contentPageKey && !action.dynamicPageKey && !action.featurePageKey) {
            return of(appNoOp());
          }

          if (action.key) {
          const cachedData = cmsData[action.key] as CmsFeatureCopy;
          if (cachedData || !cultureCode) {
            return of(appNoOp());
          }
            return this.cmsService.getFlow(action.key, cultureCode).pipe(
              map((data) => {
                if (action.key === CmsKeys.App) {
                  FeatureFlagService.useCmsConfigs(
                    (data as AppCopy).featureFlags,
                  );
                }

                return cmsCopyLoaded({
                  key: action.key,
                  data,
                });
              }),
              catchError(() =>
                of(
                  cmsCopyLoadFlowFailed({
                    key: action.key,
                  }),
                ),
              ),
            );
          } else if (action.contentPageKey) {
            const cachedData = cmsData[action.contentPageKey];
            if (cachedData || !cultureCode) {
              return of(appNoOp());
            }

            return this.cmsService.getContentPage(action.contentPageKey, cultureCode).pipe(
              map((data) => {
                return cmsCopyLoaded({
                  key: action.contentPageKey,
                  data,
                });
              }),
              catchError(() =>
                of(
                  cmsCopyLoadFlowFailed({
                    key: action.contentPageKey,
                  }),
                ),
              ),
            );

          } else if (action.featurePageKey) {
            const cachedData = cmsData[action.featurePageKey];
            if (cachedData || !cultureCode) {
              return of(appNoOp());
            }

            return this.cmsService.getFeaturePage(action.featurePageKey, cultureCode).pipe(
              map((data) => {
                return cmsCopyLoaded({
                  key: action.featurePageKey,
                  data,
                });
              }),
              catchError(() =>
                of(
                  cmsCopyLoadFlowFailed({
                    key: action.featurePageKey,
                  }),
                ),
              ),
            );
          } else if (action.dynamicPageKey) {
            const cachedData = cmsData[action.dynamicPageKey];
            if (cachedData || !cultureCode) {
              return of(appNoOp());
            }

            return this.cmsService.getDynamicPage(action.dynamicPageKey, cultureCode).pipe(
              map((data) => {
                return cmsCopyLoaded({
                  key: action.dynamicPageKey,
                  data,
                });
              }),
              catchError(() =>
                of(
                  cmsCopyLoadFlowFailed({
                    key: action.dynamicPageKey,
                  }),
                ),
              ),
            );
          } else
          {
              return of(appNoOp());
            }
        }
      ),
    );
  });

  loadCmsFlowDataFromLocal$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsCopyLoadFlowFailed),
      mergeMap((action) => {
        if (!action.key) {
          return of(appNoOp());
        }

        return this.cmsService.getFlowLocal(action.key).pipe(
          map((data) => {
            return cmsCopyLoaded({
              key: action.key,
              data,
            });
          }),
        );
      }),
    );
  });

  updateActiveDynamicPage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateActiveDynamicPage),
      map((action) => {
        return cmsLoadFlowCopy({ dynamicPageKey: action.name });
      }),
    );
  });
}
